import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  CheckCircleIcon,
  CodeIcon,
  CloudIcon,
  ToolsIcon
} from './IconComponents';

const projects = {
  energieberater: {
    title: 'Individuelle Energieberater-Lösung',
    subtitle:
      'Maßgeschneiderte Softwarelösung zur digitalen Unterstützung von Energieberatungen.',
    image: '/images/bob.png',
    mehrwertImage: '/images/bob-mehrwert.png',
    challenge:
      'Die im Beratungsprozess anfallenden Objekt-, Verbrauchs- und Energiedaten sowie Berechnungsergebnisse sollten in einer zentralen Anwendung zusammengeführt werden. Durch strukturierte Datenerfassung, automatisierte Verarbeitung und eine übersichtliche Ergebnisdarstellung entsteht eine konsistente Beratungsgrundlage, die Energieberater bei ihrer Arbeit unterstützt und Kunden transparente, nachvollziehbare Empfehlungen liefert.',
    solution:
      'Konzeption und Entwicklung einer webbasierten Lösung zur strukturierten Erfassung von Gebäudedaten. Auf Basis von Angaben wie Baujahr, Wohnfläche, Heizsystem, Energieverbrauch und Dämmstandard generiert die Anwendung automatisiert ein Energieprofil sowie passende Optimierungs- und Sanierungsansätze. Energieberater erhalten dadurch eine konsistente Datengrundlage und können Beratungsgespräche gezielt vorbereiten und individuell durchführen.',
    benefits: [
      'Zentrale und strukturierte Erfassung von Beratungsdaten',
      'Weniger manuelle Arbeit durch automatisierte Abläufe',
      'Nachvollziehbare und einheitliche Kundenberatung',
      'Skalierbare Grundlage für zukünftige Erweiterungen'
    ],
    technologies: [
      {
        icon: <CodeIcon />,
        name: 'Frontend & Backend',
        description: 'React, Java und REST-APIs'
      },
      {
        icon: <CloudIcon />,
        name: 'Cloud-Plattform',
        description: 'Azure Services und SQL Server'
      },
      {
        icon: <ToolsIcon />,
        name: 'Integration',
        description: 'Automatisierte Prozesse und Schnittstellen'
      }
    ]
  },

  'dynamics-365': {
    title: 'Microsoft Dynamics 365 Erweiterung',
    subtitle:
      'Konzeption, Aufbau und individuelle Erweiterung einer Dynamics-365-Lösung.',
    image: '/images/d365.png',
    mehrwertImage: undefined,
    challenge:
      'Die bestehenden Vertriebs- und Serviceprozesse sollten in einer zentralen CRM-Lösung gebündelt und für die Mitarbeitenden einheitlich abgebildet werden. Informationen lagen teilweise verteilt vor, manuelle Bearbeitungsschritte kosteten Zeit und eine konsistente Datenpflege war nicht immer gewährleistet. Ziel war es, transparente Abläufe zu schaffen, die Bearbeitung zu vereinfachen und eine zuverlässige Datengrundlage für das operative Geschäft zu etablieren.',
    solution:
      'Konzeption, Einrichtung und Erweiterung einer Microsoft-Dynamics-365-Lösung mit Dataverse als zentraler Datenplattform. Individuelle Tabellen, Beziehungen, Formulare und Ansichten bilden die benötigten Fachprozesse ab. Geschäftsregeln und JavaScript erweitern die Benutzerführung um dynamische Logik und Validierungen, während Power-Automate-Flows wiederkehrende Aufgaben, Benachrichtigungen und Datenverarbeitungen automatisieren.',
    benefits: [
      'An Geschäftsprozesse angepasste Benutzeroberflächen',
      'Automatisierung wiederkehrender Abläufe',
      'Verbesserte Datenqualität durch Validierungen',
      'Erweiterbare und wartbare CRM-Lösung'
    ],
    technologies: [
      {
        icon: <CodeIcon />,
        name: 'CRM-Erweiterungen',
        description: 'JavaScript, Form Scripting und Web Resources'
      },
      {
        icon: <CloudIcon />,
        name: 'Power Platform',
        description: 'Dataverse, Power Automate und Power Apps'
      },
      {
        icon: <ToolsIcon />,
        name: 'Deployment',
        description: 'Solutions, Umgebungen und ALM'
      }
    ]
  },

  'aks-cluster': {
    title: 'Azure Kubernetes Service Cluster',
    subtitle:
      'Skalierbare Plattform für den containerisierten Betrieb von Anwendungen in Azure.',
    image: '/images/AKS.png',
    mehrwertImage: undefined,
    challenge:
      'Anwendungen sollten zuverlässig, sicher und skalierbar in einer Cloud-Umgebung bereitgestellt werden. Die bisherige Bereitstellung sollte durch einen standardisierten und automatisierten Prozess ersetzt werden, um manuelle Deployments, uneinheitliche Konfigurationen und schwer nachvollziehbare Änderungen zu vermeiden. Gleichzeitig musste die Plattform flexibel auf unterschiedliche Auslastungen reagieren können und eine stabile Grundlage für den Betrieb mehrerer containerisierter Anwendungen bieten.',
    solution:
      'Konzeption und Aufbau eines Azure Kubernetes Service (AKS)-Clusters als zentrale Plattform für den containerisierten Betrieb von Anwendungen. Die Anwendungen wurden mit Docker containerisiert und über eine definierte Deployment-Strategie automatisiert in den Cluster ausgerollt. Durch getrennte Umgebungen, skalierbare Ressourcen und ein zentrales Monitoring konnten Verfügbarkeit, Transparenz und Betriebssicherheit verbessert werden. Ergänzend wurden Prozesse für CI/CD, Container-Registry, Protokollierung und die Überwachung der Workloads berücksichtigt, um Deployments reproduzierbar und effizient durchzuführen.',
    benefits: [
      'Skalierbare Bereitstellung von Container-Workloads',
      'Automatisierte Deployments über CI/CD',
      'Zentrales Monitoring und bessere Betriebsübersicht',
      'Klare Trennung von Entwicklungs-, Test- und Produktionsumgebung'
    ],
    technologies: [
      {
        icon: <CloudIcon />,
        name: 'Azure',
        description: 'Azure Kubernetes Service und Azure Container Registry'
      },
      {
        icon: <CodeIcon />,
        name: 'Container',
        description: 'Docker und Kubernetes'
      },
      {
        icon: <ToolsIcon />,
        name: 'DevOps',
        description: 'CI/CD, Monitoring und Infrastructure as Code'
      }
    ]
  }
};

const PageContainer = styled.main`
  min-height: 100vh;
  background: var(--light-background);
`;

const Hero = styled.section`
  padding: 6rem 2rem 4rem;
  text-align: center;
  color: white;
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );

  @media (max-width: 768px) {
    padding: 4.5rem 1.5rem 3rem;
  }
`;

const Container = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 4rem 2rem;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const Title = styled.h1`
  margin: 0 0 1rem;
  font-size: 3rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.3rem;
  }
`;

const Subtitle = styled.p`
  max-width: 760px;
  margin: 0 auto;
  font-size: 1.2rem;
  line-height: 1.6;
  opacity: 0.92;

  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const ProjectImage = styled.img`
  width: 100%;
  max-height: 460px;
  margin-bottom: 3rem;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.12);
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OverviewCard = styled.article`
  padding: 2rem;
  border-top: 4px solid var(--primary-color);
  border-radius: 12px;
  background: var(--card-background);
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const OverviewIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  margin-bottom: 1.5rem;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;

  svg {
    width: 26px;
    height: 26px;
  }
`;

const OverviewTitle = styled.h2`
  margin: 0 0 1rem;
  color: var(--text-color);
  font-size: 1.6rem;
`;

const OverviewText = styled.p`
  margin: 0;
  color: var(--text-color);
  line-height: 1.7;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1.5rem;
  color: var(--text-color);
  font-size: 2rem;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-bottom: 4rem;
`;

const Benefit = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1.25rem;
  border-radius: 8px;
  background: var(--card-background);
  color: var(--text-color);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);

  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    color: var(--primary-color);
  }

  span {
    line-height: 1.5;
  }
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 1.5rem;
`;

const TechCard = styled.article`
  padding: 1.5rem;
  border-radius: 10px;
  background: var(--card-background);
  color: var(--text-color);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
  }

  svg {
    width: 32px;
    height: 32px;
    margin-bottom: 1rem;
    color: var(--primary-color);
  }

  h3 {
    margin: 0 0 0.5rem;
    font-size: 1.2rem;
  }

  p {
    margin: 0;
    line-height: 1.5;
  }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-top: 3rem;
  color: var(--primary-color);
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }
`;

const BenefitsImageButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  margin: 0 0 2rem;
  border: 0;
  border-radius: 12px;
  background: transparent;
  cursor: zoom-in;

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 5px;
  }
`;

const BenefitsImage = styled.img`
  display: block;
  width: 100%;
  max-width: 780px;
  max-height: 460px;
  margin: 0 auto;
  border-radius: 12px;
  object-fit: cover;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.12);
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  z-index: 1000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(0, 0, 0, 0.82);
`;

const ImageModalContent = styled.div`
  position: relative;
  width: min(1200px, 100%);
`;

const EnlargedImage = styled.img`
  display: block;
  width: 100%;
  max-height: 85vh;
  border-radius: 12px;
  object-fit: contain;
`;

const CloseImageButton = styled.button`
  position: absolute;
  top: -0.75rem;
  right: -0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: 50%;
  background: white;
  color: #222;
  font-size: 1.8rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.3);

  &:hover {
    background: #f1f1f1;
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 3px;
  }
`;

const ProjectDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [isMehrwertImageOpen, setIsMehrwertImageOpen] = useState(false);

  const project = slug
    ? projects[slug as keyof typeof projects]
    : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMehrwertImageOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (!project) {
    return (
      <PageContainer>
        <Container>
          <SectionTitle>Projekt nicht gefunden</SectionTitle>

          <BackLink to="/#projects">
            ← Zurück zu den Projekten
          </BackLink>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Hero>
        <Title>{project.title}</Title>
        <Subtitle>{project.subtitle}</Subtitle>
      </Hero>

      <Container>
        <ProjectImage src={project.image} alt={project.title} />

        <OverviewGrid>
          <OverviewCard>
            <OverviewIcon>
              <ToolsIcon />
            </OverviewIcon>

            <OverviewTitle>Herausforderung</OverviewTitle>
            <OverviewText>{project.challenge}</OverviewText>
          </OverviewCard>

          <OverviewCard>
            <OverviewIcon>
              <CodeIcon />
            </OverviewIcon>

            <OverviewTitle>Lösung</OverviewTitle>
            <OverviewText>{project.solution}</OverviewText>
          </OverviewCard>
        </OverviewGrid>

        <SectionTitle>Mehrwert</SectionTitle>

        {project.mehrwertImage && (
          <BenefitsImageButton
            type="button"
            onClick={() => setIsMehrwertImageOpen(true)}
            aria-label="Mehrwert-Bild vergrößern"
          >
            <BenefitsImage
              src={project.mehrwertImage}
              alt="Mehrwert der individuellen Energieberater-Lösung"
            />
          </BenefitsImageButton>
        )}

        <BenefitsGrid>
          {project.benefits.map(benefit => (
            <Benefit key={benefit}>
              <CheckCircleIcon />
              <span>{benefit}</span>
            </Benefit>
          ))}
        </BenefitsGrid>

        <SectionTitle>Eingesetzte Technologien</SectionTitle>

        <TechGrid>
          {project.technologies.map(tech => (
            <TechCard key={tech.name}>
              {tech.icon}
              <h3>{tech.name}</h3>
              <p>{tech.description}</p>
            </TechCard>
          ))}
        </TechGrid>

        <BackLink to="/#projects">
          ← Zurück zu den Projekten
        </BackLink>
      </Container>

      {isMehrwertImageOpen && project.mehrwertImage && (
        <ImageModalOverlay
          role="dialog"
          aria-modal="true"
          aria-label="Vergrößerte Ansicht des Mehrwert-Bildes"
          onClick={() => setIsMehrwertImageOpen(false)}
        >
          <ImageModalContent onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}>
            <CloseImageButton
              type="button"
              onClick={() => setIsMehrwertImageOpen(false)}
              aria-label="Vergrößerte Bildansicht schließen"
            >
              ×
            </CloseImageButton>

            <EnlargedImage
              src={project.mehrwertImage}
              alt="Vergrößerte Ansicht des Mehrwerts der Energieberater-Lösung"
            />
          </ImageModalContent>
        </ImageModalOverlay>
      )}
    </PageContainer>
  );
};

export default ProjectDetail;
